import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { AccessLevel } from '../types';

export const usePermission = () => {
  const { user } = useAuth();

  const hasGroup = useCallback((groupName: string): boolean => {
    if (!user) return false;
    if (user.role === 'Admin') return true;
    
    const normalize = (s: string) => s.toLowerCase().replace(/[._\-\s]/g, '');
    const target = normalize(groupName);
    
    return user.groups.some(g => 
      normalize(g.name).includes(target) || normalize(g.email).includes(target)
    );
  }, [user]);

  const hasPermission = useCallback((key: string, level: AccessLevel = AccessLevel.ro): boolean => {
    if (!user) return false;
    
    // Admin bypass
    if (user.role === 'Admin') return true;
    
    // 1. Controllo diretto del gruppo
    if (hasGroup(key)) return true;

    // 2. Controllo gerarchico: se ho un gruppo genitore, ho accesso RO di default sui figli
    // Es: se sono nel gruppo 'app.cpgev', ho accesso RO a 'app.cpgev.qualsiasi_cosa'
    if (level === AccessLevel.ro) {
      const parts = key.split('.');
      for (let i = 2; i < parts.length; i++) {
        const parentKey = parts.slice(0, i).join('.');
        if (hasGroup(parentKey)) return true;
      }
    }
    
    // 3. Controllo fallback su permissions (se popolate)
    const userLevel = user.permissions[key] || AccessLevel.none;
    
    if (level === AccessLevel.rw) {
      return userLevel === AccessLevel.rw;
    }
    
    if (level === AccessLevel.ro) {
      return userLevel === AccessLevel.ro || userLevel === AccessLevel.rw;
    }
    
    return false;
  }, [user, hasGroup]);

  return { hasPermission, hasGroup };
};
